import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Layers, Save, RotateCcw, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Pack Manager
 *
 * Lets the admin pin exactly which songs belong in each Course Pack.
 * If no override exists, the pack falls back to alphabetical-first-N.
 */
export default function AdminPackManager() {
  const { token } = useAdminAuth();
  const [courses, setCourses] = useState([]);
  const [minSize, setMinSize] = useState(6);
  const [loading, setLoading] = useState(true);
  const [savingCourse, setSavingCourse] = useState(null);
  // course_name -> Set of selected content_ids
  const [selections, setSelections] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pack-overrides`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCourses(r.data.courses || []);
      setMinSize(r.data.min_pack_size || 6);
      const next = {};
      for (const c of r.data.courses || []) {
        next[c.course_name] = [...(c.selected_song_ids || [])];
      }
      setSelections(next);
    } catch (e) {
      toast.error("Failed to load pack data");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const toggleSong = (course, contentId) => {
    setSelections((prev) => {
      const current = prev[course] || [];
      const exists = current.includes(contentId);
      let next;
      if (exists) {
        next = current.filter((id) => id !== contentId);
      } else {
        if (current.length >= minSize) {
          toast.warning(`Course Packs must have exactly ${minSize} songs. Deselect one first.`);
          return prev;
        }
        next = [...current, contentId];
      }
      return { ...prev, [course]: next };
    });
  };

  const moveSong = (course, contentId, direction) => {
    setSelections((prev) => {
      const list = [...(prev[course] || [])];
      const idx = list.indexOf(contentId);
      if (idx === -1) return prev;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= list.length) return prev;
      [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
      return { ...prev, [course]: list };
    });
  };

  const savePack = async (course) => {
    const ids = selections[course] || [];
    if (ids.length !== minSize) {
      toast.error(`Select exactly ${minSize} songs for "${course}" before saving.`);
      return;
    }
    setSavingCourse(course);
    try {
      await axios.put(
        `${API}/admin/pack-overrides`,
        { course_name: course, song_ids: ids },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(`Pack for "${course}" saved.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingCourse(null);
    }
  };

  const resetPack = async (course) => {
    if (!window.confirm(`Reset "${course}" pack to alphabetical default? This removes your custom selection.`)) return;
    setSavingCourse(course);
    try {
      await axios.put(
        `${API}/admin/pack-overrides`,
        { course_name: course, song_ids: [] },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(`"${course}" pack reset to default.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setSavingCourse(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pack-manager">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Layers className="h-6 w-6 text-primary" />
          <h1 className="font-serif text-2xl font-bold">Course Pack Manager</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose exactly <strong>{minSize} songs</strong> per course to include in its Course Pack.
          Order matters — drag with the arrows to set the order customers see at checkout.
          Leave a course untouched to fall back to alphabetical default.
        </p>
      </div>

      {courses.map((c) => {
        const eligible = c.total_songs >= minSize;
        const selected = selections[c.course_name] || [];
        const isComplete = selected.length === minSize;
        const idSet = new Set(selected);
        const ordered = selected
          .map((id) => c.all_songs.find((s) => s.content_id === id))
          .filter(Boolean);
        return (
          <Card key={c.course_name} className="bg-card border-border" data-testid={`pack-card-${c.course_name}`}>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <CardTitle className="font-serif text-xl">{c.course_name}</CardTitle>
                  {c.is_curated ? (
                    <Badge className="bg-primary text-primary-foreground">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Curated
                    </Badge>
                  ) : (
                    <Badge variant="outline">Alphabetical default</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {c.total_songs} total songs · {selected.length} / {minSize} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {c.is_curated && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resetPack(c.course_name)}
                      disabled={savingCourse === c.course_name}
                      data-testid={`reset-pack-${c.course_name}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Reset
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => savePack(c.course_name)}
                    disabled={!isComplete || savingCourse === c.course_name}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid={`save-pack-${c.course_name}`}
                  >
                    {savingCourse === c.course_name ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Save Pack
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!eligible && (
                <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">
                    This course only has {c.total_songs} song{c.total_songs === 1 ? "" : "s"} —
                    a Course Pack needs at least {minSize}. Upload more songs to enable curation.
                  </p>
                </div>
              )}

              {/* Selected (ordered) list */}
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Pack Order (this is what customers see)
                </p>
                {ordered.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No songs selected yet.</p>
                ) : (
                  <ol className="space-y-2">
                    {ordered.map((s, i) => (
                      <li
                        key={s.content_id}
                        className="flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20"
                        data-testid={`selected-${c.course_name}-${i}`}
                      >
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{s.song_title || s.subject}</p>
                          {s.song_title && s.subject && (
                            <p className="text-xs text-muted-foreground truncate">{s.subject}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={i === 0}
                            onClick={() => moveSong(c.course_name, s.content_id, "up")}
                            className="h-7 w-7 p-0"
                            data-testid={`move-up-${s.content_id}`}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={i === ordered.length - 1}
                            onClick={() => moveSong(c.course_name, s.content_id, "down")}
                            className="h-7 w-7 p-0"
                            data-testid={`move-down-${s.content_id}`}
                          >
                            ↓
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleSong(c.course_name, s.content_id)}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            data-testid={`remove-${s.content_id}`}
                          >
                            ×
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* All available songs to pick from */}
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Available Songs ({c.all_songs.length})
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {c.all_songs.map((s) => {
                    const isSelected = idSet.has(s.content_id);
                    return (
                      <button
                        key={s.content_id}
                        type="button"
                        onClick={() => toggleSong(c.course_name, s.content_id)}
                        className={`text-left p-2.5 rounded-lg border transition-all ${
                          isSelected
                            ? "bg-primary/10 border-primary/50"
                            : "bg-background border-border hover:border-primary/40 hover:bg-primary/5"
                        }`}
                        data-testid={`song-toggle-${s.content_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`mt-0.5 inline-flex h-4 w-4 rounded border ${isSelected ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"} text-[10px] items-center justify-center flex-shrink-0`}>
                            {isSelected ? "✓" : ""}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{s.song_title || s.subject}</p>
                            {s.song_title && s.subject && (
                              <p className="text-xs text-muted-foreground truncate">{s.subject}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {courses.length === 0 && (
        <div className="text-center py-12">
          <Layers className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground text-sm">
            No courses with audio uploads yet.
          </p>
        </div>
      )}
    </div>
  );
}
